import { Box, ButtonGroup, CircularProgress, Drawer, IconButton, Typography, useTheme } from '@mui/material'
import { useCallback, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import DataLoadingSpinner from './DataLoadingSpinner'
import DocViewer, { DocViewerRenderers } from "react-doc-viewer"
import { boundaryValue, Icons, isIOS } from '../../utils/utilities'
import VideoPlayer from './video-player/VideoPlayer'
import AudioPlayer from '../exams-components/AudioPlayer'

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.mjs`

/**
 * Button component for triggering actions.
 *
 * @param {Object} props
 * @param {(File|Buffer|String)} - File accepts File object or Buffer or File URL.
 * @param {Object} props.fileDetails - File details.
 * @param {String} props.fileDetails.name - File name.
 * @param {String} props.fileDetails.type - File type.
 * @param {String} props.fileDetails.url - File URL.
 * @param {String} props.type - File type.
 * @param {String} props.loadingText - Loading text.
 * @param {Number} props.scale - Scale of the document.
 * @param {Number} props.width - Width of the document.
 * @returns {JSX.Element} The DocumentViewer component.
 */
const DocumentViewer = ( { fileDetails, file, type, loadingText = "Loading submission file...", scale, width } ) => {
    const [pages, setPages] = useState( [] )

    function onDocumentLoadSuccess ( { numPages } ) {
        const pageArray = []
        for ( let i = 1; i < numPages + 1; i++ ) {
            pageArray.push( i )
        }
        setPages( pageArray )
    }

    return (
        <Box flexGrow={1} flexShrink={0} display="flex" height="100%" alignItems="flex-start" justifyContent="center">
            {type?.toLowerCase() === 'pdf' && <Document className="pdf-viewer" onError={() => { }} loading={<DataLoadingSpinner waitingMessage={loadingText} />} file={file} onLoadSuccess={onDocumentLoadSuccess}>
                {pages.map( ( page ) => (
                    <Page scale={scale} width={width} devicePixelRatio={window.devicePixelRatio} key={page} pageNumber={page} />
                ) )}
            </Document>}
            {['mp4', 'webm'].includes( type?.toLowerCase() ) &&
                <Box maxWidth="750px" width="90%">
                    <VideoPlayer src={file} />
                </Box>}
            {['mp3', 'ogg'].includes( type?.toLowerCase() ) &&
                <Box maxWidth="250px">
                    <AudioPlayer variant="bordered" audioName={fileDetails?.name} audioSrc={file} />
                </Box>}
            {['jpeg', 'jpg', 'png'].includes( type ) && <Box flexShrink={0} bgcolor="red" width="50%" alignItems="center">
                <Box width="100%" alignItems="flex-end" style={{ objectFit: 'contain', transform: `scale(${scale})` }} flexGrow={1} component="img" src={file} />
            </Box>}
            {
                !isIOS() && !['mp3', 'ogg', 'mp4', 'webm', 'pdf', 'jpeg', 'jpg', 'png'].includes( type?.toLowerCase() ) &&
                <DocViewer
                    className={`react-doc-viewer-custom ${type}`}
                    pluginRenderers={DocViewerRenderers}
                    documents={[{ uri: file, fileType: type }]}
                />
            }
        </Box >
    )
}


export const DrawerDocumentViewer = ( { fileName, setPreviewFile, setPreviewingFileDetails, customButtons = [], fileDetails, file, type, loadingText = "Loading submission file..." } ) => {

    const { palette, border } = useTheme()

    const [pageWidthAndScale, setPageWidthScaleFactor] = useState( 1 )

    const handlePdfPageResize = useCallback( ( delta, setToFit ) => {
        if ( setToFit )
            return setPageWidthScaleFactor( { scale: 1, width: window.innerWidth > 600 ? '' : window.innerWidth - 100 } )
        setPageWidthScaleFactor( prev => {
            const newScale = boundaryValue( 0.5, ( prev.scale || 1 ) + delta, 2 )
            return { scale: newScale, width: window.innerWidth > 600 ? '' : window.innerWidth - 100 }
        } )
    }, [] )

    return (
        <Drawer anchor='bottom' sx={{ zIndex: 9000000 }} open={Boolean( file )} slotProps={{
            paper: { sx: { height: "calc(100% - 50px)" } }
        }} >
            <Box bgcolor={palette.cardBackground} flexGrow={1} display="flex" overflow="auto" flexDirection="column" borderRadius="20px 20px 0 0">
                <Box borderBottom={border[1]} alignItems="center" display="flex" justifyContent="space-between" gap="20px" padding="10px 20px">
                    <Typography variant="h1" fontWeight="600" component="p" fontSize="14px" >{fileName}</Typography>
                    <Box display="flex" gap="10px" alignItems="center">
                        {type?.toLowerCase() === 'pdf' && <ButtonGroup>
                            <IconButton size='small' sx={{ fontSize: "20px" }} id="pdf-doc-zoom-in-btn" onClick={() => handlePdfPageResize( 0.1 )}>
                                {Icons.small.ZoomIn}
                            </IconButton>
                            <IconButton size='small' sx={{ fontSize: "20px" }} id="pdf-doc-zoom-out-btn" onClick={() => handlePdfPageResize( -0.1 )}>
                                {Icons.small.ZoomOut}
                            </IconButton>
                            <IconButton size='small' sx={{ fontSize: "20px" }} id="pdf-doc-fit-btn" onClick={() => handlePdfPageResize( 1, true )}>
                                {Icons.small.CenterFocusStrongOutlined}
                            </IconButton>
                        </ButtonGroup>}
                        {customButtons.map( ( btn, btnIndex ) => {
                            if ( btn.action && btn.icon )
                                return (
                                    <IconButton size='small' sx={{ fontSize: "20px" }} key={btnIndex} onClick={btn.action}>
                                        {btn.waiting === true ? <CircularProgress size={14} /> : btn.icon}
                                    </IconButton>
                                )
                            else
                                return ""
                        } )}
                        <IconButton size='small' sx={{ fontSize: "20px" }} onClick={() => { setPreviewFile( null ); setPreviewingFileDetails( null ) }}>
                            {Icons.small.CloseIcon}
                        </IconButton>
                    </Box>
                </Box>
                <Box display="flex" flexDirection="column" overflow="auto" flexGrow={1} padding="20px" height="300px">
                    <DocumentViewer scale={pageWidthAndScale.scale} width={pageWidthAndScale.width || ''} {...{ file, fileDetails, type, loadingText }} />
                </Box>
            </Box>
        </Drawer>
    )


}

export default DocumentViewer